// Annual Book PDF engine — client-side generation with 3 themes.
// Uses jsPDF for text layout and img embedding. html2canvas is a fallback for
// tricky rendering; we favour jsPDF direct calls for crispness and print-DPI.
import jsPDF from "jspdf";

/**
 * Themes: 'classic' (serif · cream · elegant),
 *         'modern'  (sans · white · minimal),
 *         'scrapbook' (playful · warm · casual)
 */
const THEMES = {
  classic: {
    label: "Classic",
    pageBg: "#FAF7F2",
    accent: "#B8973A",
    headline: "#111D33",
    body: "#1B2A4A",
    muted: "#8C8278",
    fontHeadline: "times",
    fontBody: "times",
    ornament: "✦",
    titleSize: 42,
    chapterSize: 24,
    bodySize: 12,
    photoRadius: 0,
  },
  modern: {
    label: "Modern",
    pageBg: "#FFFFFF",
    accent: "#1B2A4A",
    headline: "#111111",
    body: "#333333",
    muted: "#888888",
    fontHeadline: "helvetica",
    fontBody: "helvetica",
    ornament: "—",
    titleSize: 38,
    chapterSize: 20,
    bodySize: 11,
    photoRadius: 0,
  },
  scrapbook: {
    label: "Scrapbook",
    pageBg: "#FFF6E4",
    accent: "#C05B3E",
    headline: "#2B2016",
    body: "#3A2F22",
    muted: "#8A6E4E",
    fontHeadline: "courier",
    fontBody: "helvetica",
    ornament: "❊",
    titleSize: 40,
    chapterSize: 22,
    bodySize: 12,
    photoRadius: 6,
    tape: true,   // draw yellowed-tape rectangles behind photos
    rotate: true, // slight rotation on photo blocks
  },
};

export function listThemes() {
  return Object.entries(THEMES).map(([id, t]) => ({ id, label: t.label }));
}

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawPageBg(pdf, theme, w, h) {
  pdf.setFillColor(theme.pageBg);
  pdf.rect(0, 0, w, h, "F");
}

async function drawCover(pdf, theme, meta) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  drawPageBg(pdf, theme, w, h);
  pdf.setDrawColor(theme.accent);
  pdf.setLineWidth(0.5);
  pdf.rect(24, 24, w - 48, h - 48);
  pdf.setFont(theme.fontHeadline, "italic");
  pdf.setTextColor(theme.accent);
  pdf.setFontSize(18);
  pdf.text(theme.ornament, w / 2, h * 0.28, { align: "center" });
  pdf.setFontSize(11);
  pdf.text(`VOLUME · ${meta.year}`, w / 2, h * 0.34, { align: "center" });
  pdf.setFont(theme.fontHeadline, "italic");
  pdf.setTextColor(theme.headline);
  pdf.setFontSize(theme.titleSize);
  const familyName = meta.familyName || "Our Family";
  pdf.text(familyName, w / 2, h * 0.5, { align: "center", maxWidth: w - 100 });
  pdf.setDrawColor(theme.accent); pdf.line(w / 2 - 20, h * 0.56, w / 2 + 20, h * 0.56);
  pdf.setFont(theme.fontBody, "italic");
  pdf.setFontSize(14);
  pdf.setTextColor(theme.muted);
  pdf.text(`The stories of ${meta.storytellerName || "our storyteller"}`, w / 2, h * 0.63, { align: "center", maxWidth: w - 120 });
  pdf.setFontSize(10);
  pdf.text("A Living Book", w / 2, h - 60, { align: "center" });
}

function drawFooter(pdf, theme, pageNum) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  pdf.setFont(theme.fontBody, "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(theme.muted);
  pdf.text(`${pageNum}`, w / 2, h - 22, { align: "center" });
}

async function drawStoryPage(pdf, theme, story, chapterInfo, pageNum) {
  pdf.addPage();
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  drawPageBg(pdf, theme, w, h);

  // Chapter label
  pdf.setFont(theme.fontHeadline, "italic");
  pdf.setTextColor(theme.accent);
  pdf.setFontSize(10);
  pdf.text(`CHAPTER ${chapterInfo.roman} · ${chapterInfo.title.toUpperCase()}`, 48, 60);

  // Story title (question)
  pdf.setFont(theme.fontHeadline, "italic");
  pdf.setTextColor(theme.headline);
  pdf.setFontSize(theme.chapterSize);
  const promptWrapped = pdf.splitTextToSize(chapterInfo.prompt || "", w - 96);
  pdf.text(promptWrapped, 48, 92);
  let cursorY = 92 + promptWrapped.length * (theme.chapterSize * 0.55) + 20;

  // Photo (if present)
  if (story.photo_url) {
    try {
      const img = await loadImage(story.photo_url);
      const maxW = w - 96;
      const maxH = 220;
      const ratio = Math.min(maxW / img.width, maxH / img.height);
      const iw = img.width * ratio;
      const ih = img.height * ratio;
      const x = (w - iw) / 2;
      if (theme.tape) {
        pdf.setFillColor("#EFDCA3");
        pdf.rect(x + iw * 0.4, cursorY - 8, 40, 12, "F");
      }
      pdf.addImage(img, "JPEG", x, cursorY, iw, ih, undefined, "FAST");
      cursorY += ih + 24;
    } catch (e) { /* silent — skip photo if fails */ }
  }

  // Body — the answer text
  pdf.setFont(theme.fontBody, "normal");
  pdf.setTextColor(theme.body);
  pdf.setFontSize(theme.bodySize);
  const answerLines = pdf.splitTextToSize(story.answer_text || "", w - 96);
  pdf.text(answerLines, 48, cursorY, { lineHeightFactor: 1.6 });
  cursorY += answerLines.length * (theme.bodySize * 1.6);

  // Follow-up (optional)
  if (story.followup_question) {
    cursorY += 16;
    pdf.setFont(theme.fontHeadline, "italic");
    pdf.setTextColor(theme.accent);
    pdf.setFontSize(11);
    const fq = pdf.splitTextToSize(story.followup_question, w - 96);
    pdf.text(fq, 48, cursorY);
    cursorY += fq.length * 14 + 6;
    if (story.followup_answer) {
      pdf.setFont(theme.fontBody, "normal");
      pdf.setTextColor(theme.body);
      pdf.setFontSize(theme.bodySize);
      const fa = pdf.splitTextToSize(story.followup_answer, w - 96);
      pdf.text(fa, 48, cursorY, { lineHeightFactor: 1.6 });
    }
  }

  drawFooter(pdf, theme, pageNum);
}

/**
 * generateAnnualBook({ stories, chapters, meta:{year,familyName,storytellerName},
 *                      themeId, onProgress: (0..1) })
 * Returns a Blob url.
 */
export async function generateAnnualBook({ stories, chapters, meta, themeId = "classic", onProgress = () => {} }) {
  const theme = THEMES[themeId] || THEMES.classic;
  const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });

  onProgress(0.02);
  await drawCover(pdf, theme, meta);
  onProgress(0.1);

  const total = stories.length || 1;
  for (let i = 0; i < stories.length; i++) {
    const s = stories[i];
    const chapter = chapters.find((c) => c.id === s.chapter_id);
    const chapterInfo = chapter
      ? { roman: chapter.roman, title: chapter.title, prompt: chapter.prompts?.[s.prompt_index] || "" }
      : { roman: "·", title: "Story", prompt: "" };
    await drawStoryPage(pdf, theme, s, chapterInfo, i + 2);
    onProgress(0.1 + (0.85 * (i + 1)) / total);
  }

  // Closing page
  pdf.addPage();
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  drawPageBg(pdf, theme, w, h);
  pdf.setFont(theme.fontHeadline, "italic");
  pdf.setTextColor(theme.accent);
  pdf.setFontSize(14);
  pdf.text(theme.ornament, w / 2, h / 2 - 30, { align: "center" });
  pdf.setFontSize(11);
  pdf.setTextColor(theme.muted);
  pdf.text('"The greatest inheritance we leave isn\'t wealth. It\'s being truly known."', w / 2, h / 2, { align: "center", maxWidth: w - 96 });
  pdf.setFontSize(9);
  pdf.text("thelivingbook.io", w / 2, h - 40, { align: "center" });

  onProgress(1);
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const filename = `TheLivingBook_${(meta.storytellerName || "Story").replace(/\s+/g, "_")}_${meta.year}.pdf`;
  return { url, filename };
}
